import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TpETLDryRun } from '../../interfaces';
import { ETLDryRunObjectTableAddColumn } from '../utils';
import { get_guid } from '../../../visual-analytics/helpers/misc';
import { VALTYPES } from '../../../../data/interfaces';
import { addETLDryRunError, getDryRunErrorsForComponent } from '../../etlDryRun';
import { FancyButton } from '../../../../components/primitives/FancyButton';

import ParamSet from '../../edit-etl-component/ParamSet';
import styles from '../../edit-etl-component/editETLComponentForm.module.scss';

// 1. Shared Types
export interface TpJsonColumnInfo {
  id: string;
  columnIdentifier: string;
  columnName: string;
  columnDescription: string;
  columnDataType: string;
}

// 2. Shared Dry Run Logic
export function applyJsonColumnsToDryRun(
  outputTableObj: any,
  columns: TpJsonColumnInfo[],
  dryRun: TpETLDryRun,
  compId: string,
) {
  for (const col of columns) {
    try {
      ETLDryRunObjectTableAddColumn(
        outputTableObj,
        col.columnIdentifier,
        col.columnName,
        col.columnDescription,
        col.columnDataType,
      );
    } catch (error) {
      addETLDryRunError(dryRun, String(error), compId, col.id);
    }
  }
}

// 3. Shared UI Component for Column Configuration
interface JsonColumnEditorListProps {
  title: string;
  description: string;
  columns: TpJsonColumnInfo[];
  dryRun: TpETLDryRun;
  compId: string;
  onChange: (newColumns: TpJsonColumnInfo[]) => void;
}

export function JsonColumnEditorList({
  title,
  description,
  columns,
  dryRun,
  compId,
  onChange,
}: JsonColumnEditorListProps) {
  function updateColumnParam(columnId: string, paramId: keyof TpJsonColumnInfo, newValue: string) {
    const updatedColumns = columns.map((col) => {
      if (col.id === columnId) {
        return { ...col, [paramId]: newValue };
      }
      return col;
    });
    onChange(updatedColumns);
  }

  function removeColumn(columnId: string) {
    onChange(columns.filter((c) => c.id !== columnId));
  }

  function addColumn() {
    const newCol: TpJsonColumnInfo = {
      id: get_guid(),
      columnIdentifier: '',
      columnName: '',
      columnDescription: '',
      columnDataType: VALTYPES.STRING,
    };
    onChange([...columns, newCol]);
  }

  return (
    <div className={styles.section}>
      <h3>{title}</h3>
      <p>{description}</p>

      {columns.map((column) => {
        const errors = getDryRunErrorsForComponent(dryRun, compId).filter((err) => err.paramId === column.id);

        return (
          <div key={column.id} className={styles.section}>
            <h4>
              Field Definition
              <button type="button" onClick={() => removeColumn(column.id)}>
                <FontAwesomeIcon icon="xmark" />
              </button>
            </h4>

            {errors.length > 0 && (
              <div className={styles.errors}>
                {errors.map((err, idx) => (
                  <div key={`err_${column.id}_${idx}`}>{err.message}</div>
                ))}
              </div>
            )}

            <ParamSet
              params={[
                {
                  name: 'JSON Object Key Identifier',
                  component: (
                    <input
                      value={column.columnIdentifier}
                      onChange={(ev) => updateColumnParam(column.id, 'columnIdentifier', ev.target.value)}
                    />
                  ),
                },
                {
                  name: 'Output Column Name',
                  component: (
                    <input
                      value={column.columnName}
                      onChange={(ev) => updateColumnParam(column.id, 'columnName', ev.target.value)}
                    />
                  ),
                },
                {
                  name: 'Description',
                  component: (
                    <input
                      value={column.columnDescription}
                      onChange={(ev) => updateColumnParam(column.id, 'columnDescription', ev.target.value)}
                    />
                  ),
                },
                {
                  name: 'Data Type',
                  component: (
                    <select
                      value={column.columnDataType}
                      onChange={(ev) => updateColumnParam(column.id, 'columnDataType', ev.target.value)}
                    >
                      {Object.entries(VALTYPES).map(([k, v]) => (
                        <option key={v} value={v}>
                          {v}
                        </option>
                      ))}
                    </select>
                  ),
                },
              ]}
            />
          </div>
        );
      })}

      <FancyButton iconName="fas:plus" onClick={addColumn}>
        Add Field
      </FancyButton>
    </div>
  );
}
